import * as Sentry from '@sentry/browser';

// 从package.json中获取版本号
import { version } from '../../../package.json';

/** 需要忽略上报信息的正则 */
const ignoreErrorsReg = [
  /ResizeObserver loop limit exceeded/i,
  /Unexpected token '<'/i, // 文件加载失败 - 1
  /expected expression, got '<'/i, // 文件加载失败 - 2
  /^t\.value$/,
  /^value$/,
  /Event `ErrorEvent` captured as exception with message `ResizeObserver loop completed with undelivered notifications.`/i,
  /ReferenceError: Can't find variable: zaloJSV2/i,
  /ReferenceError: Can't find variable: hint/i,
  /ReferenceError: Can't find variable: /i,
];

/** 是否需要忽略此信息上报 */
export const isIgnoreError = (msg: string = '') => {
  return ignoreErrorsReg.some((reg) => reg.test(msg));
};

/** 初始化Sentry监控 */
export const initSentry = () => {
  Sentry.init({
    dsn: 'https://bd49ea41284c4441ac2c674bb6de5dd1@o4505418729521152.ingest.sentry.io/4505553831002112',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    environment: process.env.NODE_ENV,
    // 错误过滤
    ignoreErrors: [
      /ResizeObserver loop limit exceeded/i,
      /Unexpected token '<'/i, // 文件加载失败 - 1
      /expected expression, got '<'/i, // 文件加载失败 - 2

      /** 以下两种报错信息本质上是 “Event `ErrorEvent` captured as exception with message `ResizeObserver loop completed with undelivered notifications.`” */
      /^t\.value$/, // 对应报错 "ResizeObserver loop completed with undelivered notifications"，是 antd 底层的 rc-table 报出来的，不影响功能
      /^value$/, // 对应报错 "ResizeObserver loop completed with undelivered notifications"，是 antd 底层的 rc-table 报出来的，不影响功能
      /ReferenceError: hint is not defined/i,
    ],

    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // 发送错误前可进行劫持
    beforeSend: function (event, hint) {
      // 报错信息中添加版本号
      event.tags = event.tags || {};
      event.tags.version = version;

      const errMsg = event?.exception?.values?.[0]?.value;

      const messages = [
        'Non-Error exception captured',
        'Object captured as promise rejection with keys: errorFields, outOfDate, values',
        'Object captured as exception with keys: errorFields, outOfDate, values',
        'Object captured as promise rejection with keys: data, msg, status',
        'Non-Error promise rejection captured with keys: errorFields, outOfDate, values',
        'Objects are not valid as a React child',
        'NotFoundError: The object can not be found here',
        'SecurityError: Only secure origins are allowed',
        'ReferenceError: hint is not defined',
      ];

      const isNonErrorException = messages.some(
        (message) =>
          errMsg?.startsWith(message) ||
          // @ts-ignore
          hint?.originalException?.message?.startsWith(message),
      );

      if (isNonErrorException) {
        return null;
      }

      if (!errMsg) {
        return null;
      }

      // 1. 过滤 非生产上报
      const patt = /^http(s|):\/\/akms-back\.bluemediagroup\.cn\/.*$/;
      if (!patt.test(event?.request?.url || '')) {
        return null;
      }

      // 2. 过滤 降噪错误
      if (isIgnoreError(errMsg)) {
        return null;
      }

      return event;
    },
  });
};
